import React, { useMemo, useState, useEffect, useRef } from "react";
import * as R from "ramda";
import { Table, Input, Icon } from "antd";
import { useTranslation } from "react-i18next";
import Fuse from "fuse.js";
import { connect } from "react-redux";

import { httpToHttps } from "../../utils/images";
import { linkToCompanies } from "../../store/actions/company";
import Button from "../Button";
import { getCountry } from "../../utils/data/static/countries";

import StatusIcon from "./StatusIcon";
import FilterTabs from "./FilterTabs";
import DropdownFilter from "./DropdownFilter";

import styled from "styled-components/macro";

export const vinculationStatuses = {
  unlinked: "UNLINKED",
  pending: "PENDING",
  linked: "ACCEPTED",
  error: "ERROR",
  rejected: "REJECTED"
};

const Wrapper = styled.div`
  font-family: "Poppins", sans-serif;
  color: #747c84;
  margin-right: 15px;
  width: 100%;
  p {
    max-width: 750px;
  }
  .description-container {
    display: flex;
    flex-direction: row;
    margin-bottom: 25px;
  }
  .rejected-row {
    background-color: rgba(255, 77, 78, 0.05);
  }
  thead th {
    font-size: 14px;
    font-weight: bold;
    color: #343a40;
    background-color: white;
    padding-top: 40px;
    > .ant-table-header-column {
      padding: 21px 0;
    }
  }
  .ant-table-tbody > tr.ant-table-row-selected td {
    background-color: #fcfff5;
    font-weight: bold;
  }
  .ant-pagination {
    width: 100%;
    text-align: center;
  }
  .empty {
    display: flex;
    flex-direction: column;
    text-align: center;
    margin: 60px auto;
    max-width: 380px;
    padding: 20px;
    border: 1px solid #efefef;
    border-radius: 4px;
  }
`;

const CompanyName = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-style: italic;
  font-size: 12px;

  img {
    max-width: 40px;
    max-height: 30px;
  }

  .icon-container {
    width: 40px;
    text-align: center;
    margin-right: 10px;
  }

  &.${vinculationStatuses.linked} {
    font-weight: bold;
  }
`;

const SaveWrapper = styled.div`
  .row {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    padding: 28px 24px;

    p {
      margin: 0 24px 0 0;
      text-align: right;
      font-size: 12px;
      color: #343a40;
    }
  }
`;

const VinculationsTable = ({
  type, // "universities" | "companies"
  data,
  tableMargin = "0 -16px",
  loading,
  onLink,
  onUnlink,
  allowLink,
  tabs,
  disableCheckboxFilter = () => false,
  translateKey = "universities",
  onChangeSelected,
  initialSelected,
  linkFilter,
  unlinkFilter,
  section,
  isDisabled,
  ...props
}) => {
  const { t } = useTranslation(["vinculations", "global"]);
  const sectionName = useMemo(() => t("vinculations:sectionName"), [t]);
  const ref = useRef(null);
  useEffect(() => {
    if (section && section === sectionName && ref.current) {
      ref.current.scrollIntoView();
    }
  }, [section, sectionName]);
  const i18n = (key, params = {}) =>
    t(`vinculations:${translateKey}.${key}`, params);

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [tableFilters, setTableFilters] = useState(null);

  useEffect(() => {
    if (initialSelected) {
      const mappedSelected = initialSelected
        .map(selectedId => {
          let key;
          const fullSelected = data.find(({ id, idCompany }) => {
            if (selectedId === id || selectedId === idCompany) {
              key = selectedId;
              return true;
            }
            return false;
          });
          if (fullSelected && typeof key !== "undefined") {
            fullSelected.key = key;
            return fullSelected;
          }
          return null;
        })
        .filter(Boolean);
      setSelected(mappedSelected);
    }
  }, [data, initialSelected]);

  const renderedData = useMemo(() => {
    const fuse = new Fuse(data, {
      shouldSort: true,
      keys: ["name"]
    });

    let filteredItems = [...data];

    if (search) {
      filteredItems = fuse.search(search);
    }

    if (statusFilter && typeof statusFilter === "function") {
      filteredItems = filteredItems.filter(statusFilter);
    }

    if (!search) {
      // Only sort alphabetically if list wasn't previously sorted by Fuse
      filteredItems = R.sortBy(
        R.compose(
          R.toLower,
          R.prop("name")
        )
      )(filteredItems);
    }

    return filteredItems;
  }, [data, statusFilter, search]);

  const companiesToBeLinked = useMemo(() => {
    return selected.filter(
      linkFilter || (({ status }) => status !== vinculationStatuses.linked)
    );
  }, [linkFilter, selected]);

  const companiesToBeUnlinked = useMemo(() => {
    return selected.filter(
      unlinkFilter || (({ status }) => status === vinculationStatuses.linked)
    );
  }, [selected, unlinkFilter]);

  useEffect(() => {
    if (onChangeSelected && typeof onChangeSelected === "function") {
      onChangeSelected(selected);
    }
  }, [onChangeSelected, selected]);

  const columns = [
    {
      title: i18n("name"),
      dataIndex: "name",
      key: "name",
      width: 500,
      render: (name, { logo, status }) => (
        <CompanyName className={status}>
          <div className="icon-container">
            {logo ? (
              <img src={httpToHttps(logo)} alt={name} />
            ) : (
              <Icon
                style={{ color: "#d0d6da", fontSize: 24 }}
                type={type === "universities" ? "bank" : "shop"}
                theme="filled"
              />
            )}
          </div>{" "}
          <span>{name}</span>
        </CompanyName>
      )
    },
    {
      title: i18n("country"),
      dataIndex: "country",
      key: "country",
      width: 150,
      filters: R.pipe(
        R.map(R.prop("country")),
        R.filter(Boolean),
        R.uniq,
        R.map(countryKey => ({
          text: getCountry(countryKey),
          value: countryKey
        }))
      )(data),
      filteredValue: R.propOr(null, "country", tableFilters),
      onFilter: (value, record) => record.country.includes(value),
      filterDropdown: DropdownFilter,
      filterIcon: <Icon type="down" />,
      render: countryId => getCountry(countryId) || countryId
    },
    {
      dataIndex: "website",
      key: "website",
      render: website =>
        website && (
          <a
            style={{ color: "#747c84" }}
            href={website}
            rel="noopener noreferrer"
            target="_blank"
          >
            {i18n("viewSite")} <Icon type="link" />
          </a>
        )
    },
    {
      dataIndex: "status",
      key: "statusIcon",
      align: "center",
      title: i18n("status"),
      render: (status, { reason }) => (
        <StatusIcon status={status} rejectedReason={reason} />
      )
    }
  ];

  return (
    <Wrapper ref={ref} {...props}>
      <h1>{i18n("title")}</h1>
      <div className="description-container">
        <p>{i18n("description")}</p>
        <Input.Search
          placeholder={i18n("search")}
          style={{ width: 354, marginLeft: "auto", maxHeight: 44 }}
          value={search}
          onChange={e => setSearch(e.target.value)}
          data-test="vinculations-search"
        />
      </div>
      <div style={{ margin: tableMargin }}>
        {tabs && tabs.length && (
          <FilterTabs
            tabs={tabs}
            onChangeFilter={filter => {
              setStatusFilter(() => filter);
              setSearch("");
            }}
            data={data}
          />
        )}
        {loading ? (
          <Table columns={columns} loading={true} />
        ) : data && !data.length && !allowLink ? (
          <div className="empty">
            <p>{t("vinculations:noVinculations")}</p>
            <a rel="noopener noreferrer" href="/compañia/universidades">
              <Button disabled={isDisabled} type="primary" icon="link">
                {t("vinculations:noVinculationsLink")}
              </Button>
            </a>
          </div>
        ) : (
          <Table
            rowSelection={{
              onChange: (keys, items) =>
                setSelected(
                  items.map((item, index) => ({ ...item, key: keys[index] }))
                ),
              getCheckboxProps: ({ status }) => ({
                disabled: disableCheckboxFilter(status)
              }),
              selectedRowKeys: selected.map(({ key }) => key)
            }}
            dataSource={renderedData}
            columns={columns}
            rowKey={item => item.id || item.idCompany || item.companyId}
            rowClassName={({ status }) =>
              status === vinculationStatuses.rejected && "rejected-row"
            }
            onChange={(_, filters) => {
              setTableFilters(filters);
            }}
            locale={{
              emptyText: data.length
                ? t("global:noResults")
                : t("vinculations:noVinculationsTable")
            }}
          />
        )}
      </div>

      {allowLink && (
        <SaveWrapper>
          <div className="row">
            <p>
              <strong>{i18n("willLink")}</strong> <br />{" "}
              {i18n("linkSelected", {
                toLink: companiesToBeLinked.length,
                total: selected.length
              })}
            </p>
            <Button
              size="lg"
              onClick={() => {
                onUnlink(companiesToBeUnlinked);
                setSelected([]);
              }}
              type="quaternary"
              icon="minus-circle"
              disabled={companiesToBeUnlinked.length <= 0}
              style={{ marginRight: 12 }}
              data-test="unlink-button"
            >
              {i18n("unlink")}
            </Button>
            <Button
              size="lg"
              onClick={() => {
                onLink(companiesToBeLinked);
                setSelected([]);
              }}
              type="primary"
              icon="check"
              disabled={companiesToBeLinked.length <= 0}
              data-test="link-button"
            >
              {i18n("linkUniversities")}
            </Button>
          </div>
        </SaveWrapper>
      )}
    </Wrapper>
  );
};

export default connect(
  state => ({
    company: R.propOr({}, "company", state)
  }),
  {
    linkToCompanies
  }
)(VinculationsTable);
